import { Component, Input } from '@angular/core';
import { Store, createSelector } from '@ngrx/store';

import { ReplaySubject, distinctUntilChanged, switchMap } from 'rxjs';

import { BaseArtifactFragment } from '../../../generated/graphql.generated';
import { getDocumentTypeLabel, getPandaDocId, isAegisArtifact, isSendableDocument } from '../../artifacts/artifact.util';
import { FreyaCommonModule } from '../../freya-common/freya-common.module';
import { DocumentHelperService } from '../../services/document-helper.service';
import { FreyaHelperService } from '../../services/freya-helper.service';
import { selectDocTypeNames } from '../../shared/create-document-dialog/document-types';
import { SharedModule } from '../../shared/shared.module';
import { createCheckSinglePermissionSelector } from '../../state/branding.store';
import { TagInfo } from '../job-invoice/job-invoice.component';
import { jobToolFeature, selectArtifactsDictionary, selectCustomer, selectJobInCurrentZone } from '../job-tool.reducer';

import { JobEstimateActions } from './job-estimate.actions';

// Send/sign lifecycle states an artifact's `status::*` attribute can carry.
// PandaDoc only ever emits `sent`/`draft`; Aegis also owns the signing and
// paid-completion states. Both engines flow through this one mapping.
type EstimateStatus = 'sent' | 'draft' | 'partially-signed' | 'signed' | 'completed';

export type TagColor = {
  backgroundColor: string;
  text: string;
}

export const selectActionsMenuArtifact = createSelector(
  selectArtifactsDictionary,
  jobToolFeature.selectActionsMenuArtifactId,
  (artifacts, artifactId) => artifacts[artifactId],
);

@Component({
  selector: 'app-job-estimate',
  standalone: true,
  imports: [
    FreyaCommonModule,
    SharedModule,
  ],
  templateUrl: './job-estimate.component.html',
  styleUrls: [
    './job-estimate.component.scss',
    '../job-financials/document-style.scss',
  ]
})
export class JobEstimateComponent {

  @Input() set artifactId(value: string) {
    this._artifactId$.next(value);
  }

  private _artifactId$ = new ReplaySubject<string>(1);

  public viewModel$ = this._artifactId$.pipe(
    distinctUntilChanged(),
    switchMap((artifactId) => this.store.select(createSelector({
      estimate: this._selectArtifactById(artifactId),
      status: this._selectStatus(artifactId),
      tags: this._selectTagsByArtifactId(artifactId),
      actionsMenu: this._selectActionsMenu,
      sendDocumentButtonVisible: createSelector(
        selectArtifactsDictionary,
        (artifacts) => isSendableDocument(artifacts[artifactId]),
      ),
      sendDocumentButtonDisabled: createSelector(
        selectCustomer,
        jobToolFeature.selectDocumentsSending,
        (customer, documentsSending) => {
          const missingContactDetails = !customer?.email && !customer?.phone;
          const sending = artifactId in documentsSending;
          return missingContactDetails || sending;
        },
      ),
      sendDocumentButtonLabel: createSelector(
        selectArtifactsDictionary,
        jobToolFeature.selectDocumentsSending,
        selectDocTypeNames,
        (artifacts, documentsSending, docTypeNames) => {

          const artifact = artifacts[artifactId];

          if (!artifact) { return; }

          const sending = artifactId in documentsSending;

          if (sending) {
            return 'Sending';
          }

          const status = this._getStatus(artifact);

          const verb = status === 'draft' ? 'Send' : 'Resend';

          const documentType = getDocumentTypeLabel(artifact, docTypeNames);

          return documentType ? `${verb} ${documentType}` : verb;
        }
      ),
      sendDocumentButtonIcon: createSelector(
        jobToolFeature.selectDocumentsSending,
        (documentsSending) => {
          if (artifactId in documentsSending) {
            return 'pi pi-spinner pi-spin';
          }
          return 'pi pi-send';
        }
      ),
    }))),
  );

  public actions = JobEstimateActions;

  constructor(
    private freyaHelper: FreyaHelperService,
    private documentHelper: DocumentHelperService,
    public store: Store,
  ) {}

  private _selectArtifactById = (artifactId: string) => createSelector(
    selectArtifactsDictionary,
    (artifacts) => artifacts[artifactId],
  );

  private _selectTagsByArtifactId = (artifactId: string) => createSelector(
    selectArtifactsDictionary,
    (artifacts) => {

      const artifact = artifacts[artifactId];

      const tags: TagInfo[] = [];

      if (!isSendableDocument(artifact)) {
        return tags;
      }

      const status = this._getStatus(artifact);

      if (status === 'draft') {
        tags.push({
          text: 'Draft',
          ...this.freyaHelper.getTagColors('neutral'),
        })
      } else {
        // sent / partially-signed / signed — the document has left draft. The
        // per-role "Signed by ..." badges below convey signing progress, so the
        // status badge stays "Sent" (matches the PandaDoc presentation).
        tags.push({
          text: 'Sent',
          ...this.freyaHelper.getTagColors('success'),
        });
      }

      if (artifact.metadata?.signedByCustomer) {
        tags.push({
          text: 'Signed by Customer',
          ...this.freyaHelper.getTagColors('success'),
        });
      }

      if (artifact.metadata?.signedByEmployee) {
        tags.push({
          text: 'Signed by Employee',
          ...this.freyaHelper.getTagColors('neutral'),
        });
      }

      return tags;
    }
  );

  private _selectStatus = (artifactId: string) => createSelector(
    selectArtifactsDictionary,
    (artifacts) => this._getStatus(artifacts[artifactId]),
  );

  private _getStatus(artifact: BaseArtifactFragment) {

    const defaultStatus = 'draft';

    if (!artifact || !artifact.attributes) {
      return defaultStatus;
    }

    const statusAttribute = artifact.attributes.find(a => a.startsWith('status'));

    if (!statusAttribute) {
      return defaultStatus;
    }

    const [ _, status ] = statusAttribute.split('::');

    if (!this._isValidStatus(status)) {
      return defaultStatus;
    }

    return status;
  }

  _selectActionsMenu = createSelector(
    selectActionsMenuArtifact,
    selectJobInCurrentZone,
    createCheckSinglePermissionSelector({ permission: 'artifacts.delete' }),
    (artifact, jobInCurrentZone, canDelete) => {

      if (!artifact) { return []; }

      // "View As" opens a role-scoped view of the document: the PandaDoc embed
      // for PandaDoc docs, the tokenized Aegis sign page for Aegis docs (the
      // effect branches by engine). Both document engines support it; plain
      // artifacts (uploads, BOLs) do not.
      const isEngineDocument = Boolean(getPandaDocId(artifact)) || isAegisArtifact(artifact);

      const minFileSize = 10;

      return [
        {
          label: 'Preview',
          icon: 'pi pi-eye',
          command: () => this.store.dispatch(this.actions.viewDocumentMenuItemClicked()),
        },
        {
          label: 'View/Sign as Employee',
          icon: 'pi pi-external-link',
          command: () => this.store.dispatch(this.actions.viewAsEmployeeMenuItemClicked()),
          visible: isEngineDocument,
        },
        {
          label: 'View/Sign as Customer',
          icon: 'pi pi-external-link',
          command: () => this.store.dispatch((this.actions.viewAsCustomerMenuItemClicked())),
          visible: isEngineDocument,
        },
        {
          label: 'Download',
          icon: 'pi pi-download',
          command: () => this.store.dispatch(this.actions.downloadDocumentMenuItemClicked()),
        },
        {
          label: 'Print',
          icon: 'pi pi-print',
          command: () => this.store.dispatch(this.actions.printMenuItemClicked()),
          visible: Boolean(this.documentHelper.getPrintableType(artifact)),
        },
        {
          separator: true,
        },
        {
          label: 'Attach to BOL',
          icon: 'pi pi-paperclip',
          command: () => this.store.dispatch(this.actions.attachToBolMenuItemClicked()),
          visible: !artifact.attributes?.includes('include-in-bol'),
          disabled: !jobInCurrentZone,
        },
        {
          label: 'Detach from BOL',
          icon: 'pi pi-times',
          command: () => this.store.dispatch(this.actions.detachFromBolMenuItemClicked()),
          visible: artifact.attributes?.includes('include-in-bol'),
          disabled: !jobInCurrentZone,
        },
        {
          label: 'Delete',
          icon: 'pi pi-trash',
          command: () => this.store.dispatch(this.actions.deleteDocumentMenuItemClicked()),
          visible: canDelete,
          disabled: !jobInCurrentZone,
        },
        {
          label: 'Open in Right Panel',
          icon: 'pi pi-external-link',
          command: () => this.store.dispatch(this.actions.openInRightPanelMenuItemClicked()),
        },
        {
          label: 'Refresh',
          icon: 'pi pi-sync',
          command: () => this.store.dispatch(this.actions.refreshMenuItemClicked()),
          visible: artifact.fileSize < minFileSize,
        }
      ];
    },
  );

  private _isValidStatus(putativeStatus: string): putativeStatus is EstimateStatus {
    return putativeStatus === 'sent'
      || putativeStatus === 'draft'
      || putativeStatus === 'partially-signed'
      || putativeStatus === 'completed'
      || putativeStatus === 'signed';
  }
}
